import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { DndContext, closestCenter, DragOverlay, CollisionDetection } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useTodoContext } from '../contexts/TodoContext';
import { groupTreeByDepth, getTotalItemCount, getCompletedItemCount, getBlockedItemCount, getResolvedNodeHeight, buildNodeLookup } from '../utils/tree';
import TodoNode from './TodoNode';
import { TodoItem } from '../types/todo';
import { LAYOUT, ZOOM, ANIMATION, MESSAGES } from '../utils/constants';
import { CustomArrow, ArrowLayer } from './CustomArrow';
import ZoomControls from './ZoomControls';
import MiniMap from './MiniMap';
import { useToastContext } from '../contexts/ToastContext';
import { useDragDrop } from '../hooks/useDragDrop';
import { useSpring, animated } from 'react-spring';
import TopTasks from './TopTasks';
import HighlightedTasks from './HighlightedTasks';
import ArchivedTaskGroup from './ArchivedTaskGroup';
import TodayPanel from './TodayPanel';
import { usePanning } from '../hooks/usePanning';

interface TodoTreeViewProps {
  filename: string;
  /** Today panel collapsed state, owned by App so it survives reloads. */
  todayPanelCollapsed?: boolean;
  onToggleTodayPanel?: () => void;
}

const TodoTreeView: React.FC<TodoTreeViewProps> = ({
  filename,
  todayPanelCollapsed = false,
  onToggleTodayPanel = () => {},
}) => {
  const { tree, actualHeights, undo, redo, canUndo, canRedo } = useTodoContext();
  const { showWarning, showInfo } = useToastContext();
  const { handleDragStart, handleDragOver, handleDragEnd, draggedItem, getDropIndicator } = useDragDrop({
    onMoveBlocked: (message) => showWarning(message, 4000),
  });
  const [arrowUpdateKey, setArrowUpdateKey] = useState(0);

  // Scroll preservation refs and state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);

  // Zoom and animation state
  const [zoom, setZoom] = useState<number>(ZOOM.DEFAULT);

  // User-controlled container height (set by dragging the resize handle).
  // When null, the height auto-fits the tree content.
  const [userHeight, setUserHeight] = useState<number | null>(null);

  // Panning functionality
  const { isPanning, panHandlers } = usePanning({
    containerRef: scrollContainerRef,
    enabled: true
  });

  const columns = useMemo(() => groupTreeByDepth(tree, undefined, actualHeights), [tree, actualHeights]);
  const { totalItems, completedItems, blockedItems } = useMemo(() => ({
    totalItems: getTotalItemCount(tree),
    completedItems: getCompletedItemCount(tree),
    blockedItems: getBlockedItemCount(tree),
  }), [tree]);

  // Zoom animation - must be called before any conditional returns
  const zoomSpring = useSpring({
    transform: `scale(${zoom})`,
    config: { tension: 300, friction: 30 },
    onRest: () => {
      // Update arrows when zoom animation completes
      setArrowUpdateKey(prev => prev + 1);
    }
  });

  const handleBlockedCompletion = (incompleteChildren: Array<{ id: string; item: TodoItem }>) => {
    const childNames = incompleteChildren.map(child => child.item.name).join(', ');
    showWarning(`Cannot mark as complete. First complete these child tasks: ${childNames}`, 5000);
  };

  const handleParentsMarkedIncomplete = (parentIds: string[]) => {
    const parentNames = parentIds.map(id => tree[id]?.name).filter(Boolean).join(', ');
    showInfo(`Parent tasks marked as incomplete: ${parentNames}`, 4000);
  };

  // Zoom functions
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * ZOOM.STEP, ZOOM.MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / ZOOM.STEP, ZOOM.MIN));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(ZOOM.DEFAULT);
  }, []);

  const handleFitToScreen = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Estimate the full tree extent. Width is column-based (same basis as the
    // minimap node layout); height comes from the treeHeight calculation below.
    const treeWidth = columns.length * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING);
    let treeBottom = 0;
    columns.forEach(column => {
      column.nodes.forEach(node => {
        treeBottom = Math.max(
          treeBottom,
          (node.yPosition || 0) + getResolvedNodeHeight(node.id, node.item, actualHeights)
        );
      });
    });

    // Account for container padding (40px on each side, see .tree-content-free-floating).
    const padding = 40;
    const availWidth = container.clientWidth - padding * 2;
    const availHeight = container.clientHeight - padding * 2;

    if (treeWidth <= 0 || treeBottom <= 0 || availWidth <= 0 || availHeight <= 0) {
      setZoom(ZOOM.DEFAULT);
    } else {
      const fitZoom = Math.min(availWidth / treeWidth, availHeight / treeBottom);
      setZoom(Math.max(ZOOM.MIN, Math.min(ZOOM.MAX, fitZoom)));
    }

    container.scrollLeft = 0;
    container.scrollTop = 0;
  }, [columns, actualHeights]);

  /**
   * Scroll a node into the middle of the viewport — used when a task is picked
   * from the Today panel, so the list and the map stay connected.
   */
  const focusNode = useCallback((id: string) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let position: { columnIndex: number; yPosition: number } | null = null;
    columns.forEach((column, columnIndex) => {
      column.nodes.forEach(node => {
        if (node.id === id) position = { columnIndex, yPosition: node.yPosition || 0 };
      });
    });
    if (!position) return;

    const { columnIndex, yPosition } = position;
    const nodeX = columnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING) + LAYOUT.TREE_PADDING;
    const nodeHeight = getResolvedNodeHeight(id, tree[id], actualHeights);

    container.scrollLeft = Math.max(0, (nodeX + LAYOUT.NODE_WIDTH / 2) * zoom - container.clientWidth / 2);
    container.scrollTop = Math.max(0, (yPosition + nodeHeight / 2) * zoom - container.clientHeight / 2);
  }, [columns, tree, actualHeights, zoom]);

  const handleMiniMapNavigate = useCallback((x: number, y: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollLeft = x;
    container.scrollTop = y;
  }, []);

  /**
   * Begin resizing the tree container by dragging the bottom-right handle.
   * Uses document-level listeners (like usePanning) so the drag keeps tracking
   * even when the cursor moves off the small handle.
   */
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Prevent the panning handler / text selection from kicking in.
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startHeight = container.clientHeight;

    const handleMove = (moveEvent: MouseEvent) => {
      const next = startHeight + (moveEvent.clientY - startY);
      setUserHeight(Math.max(LAYOUT.MIN_TREE_HEIGHT, next));
    };

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, []);

  // Update arrows when tree structure changes
  useEffect(() => {
    setArrowUpdateKey(prev => prev + 1);
  }, [tree]);

  // Update arrows when zoom changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      setArrowUpdateKey(prev => prev + 1);
    }, ANIMATION.ARROW_UPDATE_DELAY); // Small delay to let zoom animation complete
    return () => clearTimeout(timeout);
  }, [zoom]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // Undo/redo must not hijack native text-editing shortcuts while the
        // user is typing in a name/description field.
        const target = e.target as HTMLElement;
        const isTyping =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable;

        switch (e.key) {
          case '=':
          case '+':
            e.preventDefault();
            handleZoomIn();
            break;
          case '-':
            e.preventDefault();
            handleZoomOut();
            break;
          case '0':
            e.preventDefault();
            handleZoomReset();
            break;
          case 'z':
          case 'Z': // e.key is 'Z' when Shift is held
            if (isTyping) return;
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
          case 'Y':
            if (isTyping) return;
            e.preventDefault();
            redo();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleZoomReset, undo, redo]);

  /**
   * Save current scroll position before tree updates to preserve user's view
   */
  const saveScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setScrollPosition({
        x: container.scrollLeft,
        y: container.scrollTop
      });
      setShouldRestoreScroll(true);
    }
  }, []);

  /**
   * Restore scroll position after tree re-renders to prevent jumping to top
   */
  const restoreScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && shouldRestoreScroll && (scrollPosition.x > 0 || scrollPosition.y > 0)) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        container.scrollLeft = scrollPosition.x;
        container.scrollTop = scrollPosition.y;
        setShouldRestoreScroll(false);
      });
    }
  }, [scrollPosition, shouldRestoreScroll]);

  /**
   * Dynamic height for tree container based on node positions
   */
  const treeHeight = useMemo(() => {
    let maxY = 0;
    columns.forEach(column => {
      column.nodes.forEach(node => {
        const nodeBottom = (node.yPosition || 0) + getResolvedNodeHeight(node.id, node.item, actualHeights);
        maxY = Math.max(maxY, nodeBottom);
      });
    });
    return Math.max(LAYOUT.MIN_TREE_HEIGHT, maxY + LAYOUT.PARENT_SPACING);
  }, [columns, actualHeights]);

  // Minimap data - create simplified node data for minimap
  const miniMapNodes = useMemo(() => columns.flatMap((column, columnIndex) =>
    column.nodes.map(node => ({
      id: node.id,
      x: columnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING),
      y: node.yPosition || 0,
      width: LAYOUT.NODE_WIDTH,
      height: getResolvedNodeHeight(node.id, node.item, actualHeights),
      status: (node.item.done ? 'done' : node.item.blocked ? 'blocked' : node.item.archived ? 'archived' : 'pending') as 'done' | 'blocked' | 'pending' | 'archived',
      priority: (node.item.priority || 'low') as 'low' | 'medium' | 'high' | 'critical'
    }))
  ), [columns, actualHeights]);

  // Save scroll position before tree changes
  const treeSize = Object.keys(tree).length;
  useEffect(() => {
    saveScrollPosition();
  }, [treeSize, saveScrollPosition]); // Trigger when tree structure changes

  // Restore scroll position after nodes render
  useEffect(() => {
    restoreScrollPosition();
  }, [columns, restoreScrollPosition]);

  /**
   * Custom collision detection for drag-and-drop.
   *
   * A node may be dropped onto:
   * - one of its siblings (to reorder within the same parent), or
   * - any other node (to re-parent it, and its subtree, under that node).
   *
   * The only invalid drop targets are the dragged node itself and its
   * descendants, since re-parenting into those would create a cycle. We
   * exclude them here so the user never gets feedback suggesting an
   * impossible drop.
   */
  const customCollisionDetection: CollisionDetection = useCallback((args) => {
    const { active } = args;

    if (!active) return [];

    const activeId = active.id as string;

    // Collect the dragged node and all of its descendants - these cannot be
    // drop targets without breaking the tree.
    const invalidTargets = new Set<string>([activeId]);
    const queue = [activeId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const currentItem = tree[currentId];
      if (!currentItem) continue;
      currentItem.children.forEach(childId => {
        if (!invalidTargets.has(childId)) {
          invalidTargets.add(childId);
          queue.push(childId);
        }
      });
    }

    // Let dnd-kit pick the closest droppable, then drop any invalid targets.
    return closestCenter(args).filter(collision =>
      tree[collision.id as string] !== undefined && !invalidTargets.has(collision.id as string)
    );
  }, [tree]);

  if (columns.length === 0) {
    return (
      <div className="todo-tree-view">
        <div className="tree-error">
          <h3>Invalid Todo Tree</h3>
          <p>Could not find a valid root node named "Everything".</p>
        </div>
      </div>
    );
  }

  const renderNodes = () => {
    const allNodes: React.ReactElement[] = [];
    const allArrows: React.ReactElement[] = [];
    const nodeLookup = buildNodeLookup(columns);

    columns.forEach((column, columnIndex) => {
      column.nodes.forEach((node, nodeIndex) => {
        // Add the regular node
        allNodes.push(
          <TodoNode
            key={node.id}
            id={node.id}
            item={{ ...node.item, yPosition: node.yPosition }}
            hasChildren={node.childrenIds.length > 0}
            nodeIndex={nodeIndex}
            columnIndex={columnIndex}
            dropIndicator={getDropIndicator(node.id)}
            onBlockedCompletion={handleBlockedCompletion}
            onParentsMarkedIncomplete={handleParentsMarkedIncomplete}
          />
        );

        // Add archived task group if this node has archived children
        if (node.archivedChildrenIds.length > 0) {
          const nodeHeight = getResolvedNodeHeight(node.id, node.item, actualHeights);
          const archivedGroupYPosition = (node.yPosition || 0) + nodeHeight + LAYOUT.ARCHIVED_GAP;

          allNodes.push(
            <ArchivedTaskGroup
              key={`archived-${node.id}`}
              archivedTaskIds={node.archivedChildrenIds}
              tree={tree}
              parentId={node.id}
              yPosition={archivedGroupYPosition}
              columnIndex={columnIndex}
            />
          );

          // Add arrow from parent to archived group
          const parentRect = {
            x: columnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING) + LAYOUT.TREE_PADDING,
            y: node.yPosition || 0,
            width: LAYOUT.NODE_WIDTH,
            height: nodeHeight
          };
          const archivedRect = {
            x: columnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING) + LAYOUT.TREE_PADDING,
            y: archivedGroupYPosition,
            width: LAYOUT.NODE_WIDTH,
            height: LAYOUT.ARCHIVED_GROUP_HEIGHT
          };
          allArrows.push(
            <CustomArrow
              key={`${node.id}-archived-group`}
              startId={node.id}
              endId={`archived-group-${node.id}`}
              startRect={parentRect}
              endRect={archivedRect}
              color="var(--state-archived)"
              strokeWidth={1}
              headSize={4}
              startAnchor="bottom"
              endAnchor="top"
              curveness={0.3}
              dashness={true}
              variant="archived"
            />
          );
        }

        // Add arrows to active children - O(1) lookup per child
        node.childrenIds.forEach(childId => {
          const childEntry = nodeLookup.get(childId);

          if (childEntry) {
            const childNode = childEntry.node;
            const childColumnIndex = childEntry.columnIndex;
            const startRect = {
              x: columnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING) + LAYOUT.TREE_PADDING,
              y: node.yPosition || 0,
              width: LAYOUT.NODE_WIDTH,
              height: getResolvedNodeHeight(node.id, node.item, actualHeights)
            };
            const endRect = {
              x: childColumnIndex * (LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING) + LAYOUT.TREE_PADDING,
              y: childNode.yPosition || 0,
              width: LAYOUT.NODE_WIDTH,
              height: getResolvedNodeHeight(childNode.id, childNode.item, actualHeights)
            };
            allArrows.push(
              <CustomArrow
                key={`${node.id}-${childId}`}
                startId={node.id}
                endId={childId}
                startRect={startRect}
                endRect={endRect}
                color="var(--interactive-primary)"
                strokeWidth={2}
                headSize={6}
                startAnchor="right"
                endAnchor="left"
                curveness={0.8}
                variant="normal"
                priority={childNode.item.priority || 'low'}
              />
            );
          }
        });
      });
    });

    return { nodes: allNodes, arrows: allArrows };
  };

  const allNodeIds = Object.keys(tree);
  const { nodes, arrows } = renderNodes();


  return (
    <DndContext
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="todo-tree-view">
        <div className="tree-header">
          <div className="tree-header-row">
            <h3 className="tree-title">Todo Tree: <span className="tree-filename">{filename}</span></h3>
            <div className="tree-history-controls">
              <button
                className="history-button"
                onClick={undo}
                disabled={!canUndo}
                title={MESSAGES.UNDO_TOOLTIP}
                aria-label="Undo"
              >
                ↶ Undo
              </button>
              <button
                className="history-button"
                onClick={redo}
                disabled={!canRedo}
                title={MESSAGES.REDO_TOOLTIP}
                aria-label="Redo"
              >
                ↷ Redo
              </button>
            </div>
            <div className="tree-stats">
              <span className="stat stat-total">Total: {totalItems}</span>
              <span className="stat stat-done">Done: {completedItems}</span>
              <span className="stat stat-blocked">Blocked: {blockedItems}</span>
              <span className="stat stat-progress">Progress: {totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0}%</span>
            </div>
          </div>
        </div>

        <SortableContext items={allNodeIds} strategy={rectSortingStrategy}>
          <div className="tree-with-today">
          <div className="tree-container-with-zoom">
            <div
              ref={scrollContainerRef}
              className={`tree-content-free-floating ${isPanning ? 'is-panning' : ''}`}
              style={{
                height: `${userHeight ?? Math.min(treeHeight, window.innerHeight * 0.75)}px`,
                cursor: isPanning ? 'grabbing' : 'grab'
              }}
              {...panHandlers}
            >
              <animated.div
                style={zoomSpring}
                className="tree-zoom-wrapper"
              >
                {nodes}
                <ArrowLayer key={arrowUpdateKey}>
                  {arrows}
                </ArrowLayer>
              </animated.div>
            </div>

            <div
              className="tree-resize-handle"
              onMouseDown={handleResizeStart}
              title="Drag to resize tree window"
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize tree window"
            />

            <ZoomControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onZoomReset={handleZoomReset}
              onFitToScreen={handleFitToScreen}
            />

            <MiniMap
              treeContainerRef={scrollContainerRef}
              zoom={zoom}
              onNavigate={handleMiniMapNavigate}
              nodes={miniMapNodes}
            />
          </div>

          <TodayPanel
            isCollapsed={todayPanelCollapsed}
            onToggleCollapsed={onToggleTodayPanel}
            onFocusNode={focusNode}
          />
          </div>
        </SortableContext>

        <DragOverlay>
          {draggedItem && tree[draggedItem] ? (
            <TodoNode
              id={draggedItem}
              item={tree[draggedItem]}
              hasChildren={tree[draggedItem].children.length > 0}
              isDraggable={false}
            />
          ) : null}
        </DragOverlay>

        <TopTasks tree={tree} />
        <HighlightedTasks tree={tree} />

      <div className="tree-legend">
        <div className="tree-legend-row">
          <span className="tree-legend-label">Legend:</span>
          <span className="legend-item">✓ Done</span>
          <span className="legend-item">🚫 Blocked</span>
          <span className="legend-item">📦 Archived</span>
          <span className="legend-item">○ Pending</span>
        </div>
      </div>

      </div>
    </DndContext>
  );
};

export default TodoTreeView;